import type { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { UseFieldArrayMove } from 'react-hook-form';
import { shouldHandleDrop } from '@/components/common/settings/item-types/lib/utils';

export function reorderDndEntries<T>({
  items,
  activeId,
  overId,
  getId,
}: {
  items: T[];
  getId: (input: T) => number;
  activeId: number;
  overId: number;
}) {
  const buffer = [...items];
  const activeIndex = buffer.findIndex((item) => getId(item) === activeId);
  const overIndex = buffer.findIndex((item) => getId(item) === overId);

  if (activeIndex !== -1 && overIndex !== -1) {
    return arrayMove(buffer, activeIndex, overIndex);
  }
  return buffer;
}

export type DndOrderBody =
  | {
      beforeId: number;
      afterId?: number | null;
    }
  | {
      beforeId?: number | null;
      afterId: number;
    };

export function getDndUpdateBody<T>({
  entries,
  setEntries,
  setActiveId,
  getId,
  event,
}: {
  entries: T[];
  setEntries: (entriesInput: T[]) => void;
  setActiveId: (id: number | null) => void;
  getId: (item: T) => number;
  event: DragEndEvent;
}): { id: number; data: DndOrderBody } | undefined {
  const { active, over } = event;

  if (shouldHandleDrop(active, over)) {
    const reorderedEntries =
      active.id &&
      over?.id &&
      reorderDndEntries({
        items: entries,
        activeId: Number(active.id),
        overId: Number(over.id),
        getId,
      });

    if (reorderedEntries) {
      setEntries(reorderedEntries);

      const activeIndex = reorderedEntries.findIndex((entry) => getId(entry) === active.id);

      const shouldReorderAfterId = activeIndex - 1 >= 0;
      const beforeIndexItem = reorderedEntries[activeIndex - 1];
      if (shouldReorderAfterId && beforeIndexItem) {
        return {
          id: Number(active.id),
          data: { afterId: Number(getId(beforeIndexItem)) },
        };
      } else {
        const shouldReorderBeforeId = activeIndex + 1 < reorderedEntries.length;
        const afterIndexItem = reorderedEntries[activeIndex + 1];

        if (shouldReorderBeforeId && afterIndexItem) {
          return {
            id: Number(active.id),
            data: { beforeId: Number(getId(afterIndexItem)) },
          };
        }
      }
    }
  }
  setActiveId(null);
  return undefined;
}

export function toRelativePriority(data: DndOrderBody): {
  id: number | null;
  type: 'before' | 'after';
} {
  return 'afterId' in data && data.afterId != null
    ? { id: data.afterId, type: 'after' }
    : { id: data.beforeId ?? null, type: 'before' };
}

export function moveEntriesAfterDnD<T>({
  entries,
  move,
  setActiveId,
  getId,
  event,
}: {
  entries: T[];
  move: UseFieldArrayMove;
  setActiveId: (id: number | null) => void;
  getId: (item: T) => UniqueIdentifier;
  event: DragEndEvent;
}): { id: UniqueIdentifier; data: DndOrderBody } | undefined {
  const { active, over } = event;

  if (shouldHandleDrop(active, over)) {
    const activeIndex = entries.findIndex((item) => getId(item) === active.id);
    const overIndex = entries.findIndex((item) => getId(item) === over?.id);

    if (activeIndex !== -1 && overIndex !== -1 && active.id && over?.id) {
      move(activeIndex, overIndex);
    }
  }
  setActiveId(null);
  return undefined;
}
